import React, { RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Direction, EventName, KitInputProps } from '../../../common/types';
import { KeyConstants } from '../../../common/constants';
import { isKey, clamp } from '../../../common/utils/KitCommon';
import { RequiredIdentifier } from '../formElements';
import RangeHandle, { RangeHandleProps } from './RangeHandle';
import {
  StyledRangeWrapper,
  RangeLabel,
  StyledLabel,
  ControlsWrapper,
  StyledMinMaxLabel,
  StyledTrack,
  TrackSection,
  ResetLink,
  FormInputError,
} from './styles';

const { DOWN, UP, LEFT, RIGHT, PAGE_DOWN, PAGE_UP, HOME, END } = KeyConstants;

/** Return closest step to calculated position */
const getNearestStep = (val: number, step: number) => {
  if (step > val) {
    return 0;
  }
  const prevStep = Math.floor(val / step) * step;
  const nextStep = Math.ceil(val / step) * step;
  if (Math.abs(val - prevStep) < Math.abs(val - nextStep)) {
    return prevStep;
  }
  return nextStep;
};

const getCoords = (el: HTMLDivElement) => {
  const rect: DOMRect = el.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
  };
};

const arrayEquals = (a: (string | number)[], b: (string | number)[]): boolean => {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
};

export interface KitRangeProps extends KitInputProps {
  handles: RangeHandleProps[];
  thumbSize?: number;
  min: number;
  max: number;
  reset?: boolean;
  showReset?: boolean; // Show 'Clear' button, regardless of whether
  // values have changed
  step?: number;
  showMinMaxLabels?: boolean;
  floatingPoint?: boolean;
  rangeLabelFormatter?: (values: number[]) => string;
  onChange?: (val: number | number[]) => void;
  strings?: Record<string, string>;
  defaultValue: number | number[];
}

const KitRange: React.FC<KitRangeProps> = ({
  defaultValue,
  disabled,
  error,
  floatingPoint = false,
  id,
  label,
  min = 0,
  max = 0,
  name,
  onChange,
  rangeLabelFormatter,
  required,
  reset,
  showReset,
  showMinMaxLabels,
  step = 1,
  thumbSize = 15,
  strings = { reset: 'Clear' },
  handles,
}) => {
  // defaults
  const trackHeight = 3;
  const defaultTrackWidth = 300;
  const containerRef: RefObject<HTMLDivElement> = useRef();
  const trackRef: RefObject<HTMLDivElement> = useRef();
  const [currentValue, setCurrentValue] = useState(defaultValue);
  const inputErrorId = label ? `${label.split(' ').join('')}_error` : undefined;
  const handlesRef = useRef<HTMLDivElement[]>([]);
  const tracksRef = useRef<HTMLDivElement[]>([]);

  const sliderHandles: RangeHandleProps[] = useMemo(() => {
    return handles.map((handle, i) => {
      return {
        ...handle,
        value: Array.isArray(defaultValue) ? defaultValue[i] : defaultValue,
        disabled,
        index: i,
        size: thumbSize,
        min,
        max,
        error,
      };
    });
  }, [handles, defaultValue, disabled, error, max, min, thumbSize]);

  const createTracks = (items: RangeHandleProps[]) => {
    const tracks = items.slice(items.length - 1).map((item, i) => ({
      ...item,
      trackHeight,
      index: i,
    }));
    return tracks;
  };

  const getValues = useCallback(() => {
    return sliderHandles.map((handle, index) => {
      const el = handlesRef.current[index];
      if (el) {
        return parseFloat(el.getAttribute('data-value'));
      }
      return handle.value;
    });
  }, [sliderHandles]);

  const valueLabelRef = useRef<HTMLDivElement>();
  const valueTracks = createTracks(handles);

  const updateRange = useCallback(
    (noEmit?: boolean) => {
      const values = getValues();

      const rangeLabel: string = (() => {
        if (rangeLabelFormatter) {
          return rangeLabelFormatter(values);
        }
        const labels: string[] = values.map((val) => `${val}`);
        return labels.join(' - ');
      })();

      valueLabelRef.current.innerHTML = rangeLabel;
      setCurrentValue(values);
      if (onChange && !noEmit) {
        onChange(values);
      }
    },
    [getValues, onChange, rangeLabelFormatter]
  );

  const getNearestHandle = (value: number): HTMLDivElement => {
    let nearestHandleValue = Infinity;
    const nearestHandle = handlesRef.current.reduce((acc, handle) => {
      const handleValue = parseInt(handle.getAttribute('data-value'), 10);
      if (Math.abs(value - handleValue) < nearestHandleValue) {
        nearestHandleValue = Math.abs(value - handleValue);
        return handle;
      }
      return acc;
    }, handlesRef.current[0]);
    return nearestHandle;
  };

  const getTrackWidth = useCallback(() => {
    return Math.round(trackRef.current?.getBoundingClientRect().width) ?? defaultTrackWidth;
  }, [trackRef]);

  /**
   * Returns the min / max boundaries that a handle value can represent
   * This prevents user from being able to drag the min handle past the max handle and vice versa
   * @param handleIndex
   */
  const getHandleBounds = useCallback(
    (handleIndex: number): Record<string, number> => {
      const minBound: number =
        handleIndex === 0
          ? min
          : parseFloat(handlesRef.current[handleIndex - 1].getAttribute('data-value'));
      const maxBound: number =
        handleIndex === 0 && sliderHandles.length > 1
          ? parseFloat(handlesRef.current[handleIndex + 1].getAttribute('data-value'))
          : max;

      return { min: minBound, max: maxBound };
    },
    [min, max, sliderHandles.length]
  );

  const getTrackOffset = useCallback(
    (trackIndex, dir) => {
      const trackOffset: number = getCoords(trackRef.current).left;
      const h1 = handlesRef.current[trackIndex];
      if (sliderHandles.length > trackIndex + 1) {
        const h2 = handlesRef.current[trackIndex + 1];
        return dir === Direction.LEFT
          ? getCoords(h1).left - trackOffset
          : getCoords(h2).left - trackOffset;
      }
      return dir === Direction.LEFT ? 0 : getCoords(h1).left - trackOffset;
    },
    [sliderHandles.length]
  );

  const renderTracks = useCallback(() => {
    tracksRef.current.forEach((el, i) => {
      const left = getTrackOffset(i, Direction.LEFT);
      const right = getTrackWidth() - getTrackOffset(i, Direction.RIGHT);
      el.style.left = `${left}px`;
      el.style.right = `${right}px`;
    });
  }, [getTrackOffset, getTrackWidth]);

  const moveHandleTo = useCallback(
    (handle: HTMLDivElement, value: number, noEmit?: boolean): void => {
      const index = parseInt(handle.getAttribute('data-index'), 10);
      const bounds = getHandleBounds(index);
      const normalizedValue = clamp(value, bounds.min, bounds.max);
      let labelValue = value.toString();

      labelValue = floatingPoint
        ? normalizedValue.toString()
        : Math.round(normalizedValue).toString();

      handle.setAttribute('data-value', labelValue);
      handle.setAttribute('aria-valuenow', labelValue);
      handle.setAttribute('aria-valuetext', labelValue);

      const trackWidth = getTrackWidth();
      const handleOffset = thumbSize / 2;
      const pos = Math.round((normalizedValue * trackWidth) / (max - min)) - handleOffset;
      const maxX = trackWidth - handleOffset * 2;
      const clampX = clamp(pos, 0, maxX);
      handle.style.left = `${clampX}px`;

      renderTracks();
      updateRange(noEmit);
    },
    [floatingPoint, min, max, renderTracks, updateRange, getHandleBounds, getTrackWidth, thumbSize]
  );

  const handleReset = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    sliderHandles.forEach((_, i) => {
      const value = (() => {
        if (defaultValue) {
          return Array.isArray(defaultValue) ? [min, max][i] : min;
        }
        return i === 0 ? min : max;
      })();
      moveHandleTo(handlesRef.current[i], value);
    });
  };

  const handleResetKeyPress = (e) => {
    if (isKey(['ENTER', 'SPACE'], e)) {
      handleReset(e);
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      let preventDefault = false;
      const el = e.target as HTMLDivElement;
      const val = parseInt(el.getAttribute('data-value'), 10);
      // not really sure what these increments should be
      const pageIncrement: number = max * 0.1;
      const keyTests: Record<string, string[] | number | unknown>[] = [
        { keys: [LEFT, DOWN], value: val - step },
        { keys: [RIGHT, UP], value: val + step },
        { keys: [PAGE_DOWN], value: val - step * pageIncrement },
        { keys: [PAGE_UP], value: val + step * pageIncrement },
        { keys: [HOME], value: min },
        { keys: [END], value: max },
      ];

      keyTests.some((test) => {
        if (isKey(test.keys as string[], e)) {
          preventDefault = true;
          moveHandleTo(el, test.value as number);
          return true;
        }
        return false;
      });

      if (preventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [min, max, step, moveHandleTo]
  );

  const handleFocus = (e: FocusEvent) => {
    const target = e.target as HTMLDivElement;
    target.classList.add('focus');
    trackRef.current.classList.add('focus');
  };

  const handleBlur = (e: FocusEvent) => {
    const target = e.target as HTMLDivElement;
    target.classList.remove('focus');
    trackRef.current.classList.remove('focus');
  };

  const handleMouseDown = useCallback(
    (evt: MouseEvent) => {
      const target = evt.target as HTMLDivElement;
      const handle = target as HTMLDivElement;
      const trackNode = trackRef.current;

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const diffX: number = e.clientX - getCoords(trackNode).left;
        const value: number = min + ((max - min) * diffX) / getTrackWidth();
        const steppedValue = floatingPoint ? value : getNearestStep(value, step);
        moveHandleTo(handle, steppedValue);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      evt.preventDefault();
      evt.stopPropagation();

      handle.focus();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveHandleTo, floatingPoint, min, max, step]
  );

  const handleTrackClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const trackNode = trackRef.current;
      const diffX: number = e.clientX - getCoords(trackNode).left;
      const value: number = min + ((max - min) * diffX) / getTrackWidth();

      const handle = getNearestHandle(value);
      moveHandleTo(handle, value);
    },
    [min, max, getTrackWidth, moveHandleTo]
  );

  const handleResize = useCallback(() => {
    handlesRef.current.forEach((el) => {
      const value = parseFloat(el.getAttribute('data-value'));
      moveHandleTo(el, value);
    });
  }, [moveHandleTo]);

  const valuesAreEqual = (value1, value2) => {
    if (Array.isArray(value1) && Array.isArray(value2)) {
      return arrayEquals(value1, value2);
    }
    return value1 === value2;
  };

  useLayoutEffect(() => {
    const init = () => {
      window.addEventListener(EventName.RESIZE, handleResize);

      if (!disabled) {
        trackRef.current.addEventListener('click', handleTrackClick);

        sliderHandles.forEach((handle, index) => {
          const el = handlesRef.current[index];
          const value = parseFloat(el.getAttribute('data-value')) ?? handle.value;
          el.tabIndex = 0;
          el.addEventListener('keydown', handleKeyDown);
          el.addEventListener('mousedown', handleMouseDown);
          el.addEventListener('focus', handleFocus);
          el.addEventListener('blur', handleBlur);
          el.setAttribute('data-value', value.toString());
          moveHandleTo(el, value, true);
        });
      }
    };

    init();

    const sliderTrackRef = trackRef.current;
    const sliderHandlesRef = handlesRef.current;

    return () => {
      window.removeEventListener(EventName.RESIZE, handleResize);
      if (sliderTrackRef) {
        sliderTrackRef.removeEventListener('click', handleTrackClick);
      }
      sliderHandlesRef.forEach((el) => {
        if (el) {
          el.removeEventListener('keydown', handleKeyDown);
          el.removeEventListener('mousedown', handleMouseDown);
          el.removeEventListener('focus', handleFocus);
          el.removeEventListener('blur', handleBlur);
        }
      });
    };
  }, [
    disabled,
    sliderHandles,
    moveHandleTo,
    handleKeyDown,
    handleMouseDown,
    handleResize,
    handleTrackClick,
  ]);

  return (
    <StyledRangeWrapper
      data-name={name}
      ref={containerRef}
      id={id}
      className={classNames({ disabled })}
      disabled={disabled}
      aria-disabled={disabled}
    >
      {label && (
        <StyledLabel error={error}>
          {label}
          {required && <RequiredIdentifier />}
        </StyledLabel>
      )}
      <RangeLabel thumbSize={thumbSize} ref={valueLabelRef} />
      <ControlsWrapper>
        {showMinMaxLabels && <StyledMinMaxLabel>{min}</StyledMinMaxLabel>}
        <div>
          <StyledTrack className='slider-track' ref={trackRef} trackHeight={trackHeight}>
            {valueTracks.map((trackSection, index) => (
              <TrackSection
                key={`${trackSection.index}`}
                className='track-section'
                ref={(el) => {
                  tracksRef.current[index] = el;
                }}
                {...trackSection}
              />
            ))}
            {sliderHandles.map((handle, index) => (
              <RangeHandle
                key={`${handle.index}`}
                describedBy={inputErrorId}
                ref={(el) => {
                  handlesRef.current[index] = el;
                }}
                {...handle}
              />
            ))}
          </StyledTrack>
        </div>
        {showMinMaxLabels && <StyledMinMaxLabel max>{max}</StyledMinMaxLabel>}
        {((!valuesAreEqual(currentValue, defaultValue) && reset) || showReset) && (
          <ResetLink
            role='button'
            tabIndex={0}
            onClick={handleReset}
            onKeyPress={handleResetKeyPress}
          >
            {strings.reset}
          </ResetLink>
        )}
      </ControlsWrapper>
      {error && <FormInputError id={inputErrorId}>{error}</FormInputError>}
    </StyledRangeWrapper>
  );
};

export default KitRange;
